import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { apiFetch, getApiUrl } from '../../config/api';
import './SalesCostingOverview.css';
import { formatAppDate } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const SUB_TABS = [
  { id: 'by-order', labelKey: 'profitMarginsOverview.tabs.byOrder' },
  { id: 'by-product', labelKey: 'profitMarginsOverview.tabs.byProduct' },
  { id: 'by-customer', labelKey: 'profitMarginsOverview.tabs.byCustomer' },
];

const ProfitMarginsOverview = forwardRef(({ period = 'month' }, ref) => {
  const { t } = useTranslation('production');
  const [activeView, setActiveView] = useState('by-order');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(period);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async (view = activeView) => {
    try {
      setRefreshing(true);
      setError(null);

      let url = `/api/costing/margins/${view}`;
      if (useCustomDates && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?period=${selectedPeriod}`;
      }

      const response = await apiFetch(getApiUrl(url));
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError(t('profitMarginsOverview.fetchFailed'));
      }
    } catch (err) {
      console.error('Error fetching profit margins:', err);
      setError(t('costing.networkError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!useCustomDates) {
      fetchData();
    }
  }, [selectedPeriod, activeView]);

  useEffect(() => {
    if (useCustomDates && startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, useCustomDates, activeView]);

  useImperativeHandle(ref, () => ({
    refresh: fetchData,
    isRefreshing: refreshing
  }));

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatPercentage = (value) => {
    const num = parseFloat(value) || 0;
    return `${num.toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getMarginClass = (percentage) => {
    const pct = parseFloat(percentage) || 0;
    if (pct < 0) return 'negative';
    if (pct < 10) return 'low';
    if (pct < 20) return 'medium';
    return 'good';
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'SHIPPED':
      case 'DELIVERED':
      case 'COMPLETED':
        return 'status-success';
      case 'CONFIRMED':
      case 'IN_PRODUCTION':
        return 'status-info';
      case 'PENDING':
      case 'DRAFT':
        return 'status-warning';
      case 'CANCELLED':
        return 'status-error';
      default:
        return 'status-default';
    }
  };

  const handleViewChange = (view) => {
    if (view === activeView) {
      fetchData(view);
      return;
    }
    setActiveView(view);
    setData(null);
    setLoading(true);
  };

  if (loading) {
    return (
      <div className="sales-costing-overview loading">
        <div className="loading-spinner"></div>
        <p>{t('profitMarginsOverview.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-costing-overview error">
        <p>{error}</p>
        <button onClick={() => fetchData()} className="btn-retry">{t('common:ui.tryAgain')}</button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="sales-costing-overview empty">
        <p>{t('profitMarginsOverview.noData')}</p>
      </div>
    );
  }

  const { summary } = data;

  // REAL / INVENTARIO / ESTIMADO_BOM: where the cost comes from (#313)
  // INVENTARIO stays provisional until the stock is shipped
  const renderCostSource = (source, provisional) => {
    if (!source) return null;
    const key = source === 'INVENTARIO' && provisional ? 'INVENTARIO_PROVISIONAL' : source;
    return (
      <span className={`cost-source-badge cost-source-${source.toLowerCase()}${provisional ? ' cost-source-provisional' : ''}`}
            title={t(`profitMarginsOverview.costSourceHint.${key}`)}>
        {t(`profitMarginsOverview.costSource.${key}`)}
      </span>
    );
  };

  const renderOrdersTable = () => {
    const orders = data.orders || [];
    if (orders.length === 0) {
      return <div className="empty-state">{t('costing.noOrdersForPeriod')}</div>;
    }
    return (
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t('costing.columns.orderNumber')}</th>
              <th>{t('common:ui.customer')}</th>
              <th>{t('costing.columns.products')}</th>
              <th>{t('common:ui.date')}</th>
              <th className="text-right">{t('costing.columns.revenue')}</th>
              <th className="text-right">{t('costing.columns.bomCost')}</th>
              <th className="text-right">{t('costing.columns.actualCost')}</th>
              <th className="text-right">{t('costing.columns.waste')}</th>
              <th className="text-right">{t('costing.columns.totalCost')}</th>
              <th>{t('costing.columns.costSource')}</th>
              <th className="text-right">{t('costing.columns.margin')}</th>
              <th className="text-right">{t('costing.columns.marginPct')}</th>
              <th>{t('common:ui.status')}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, i) => {
              const marginClass = getMarginClass(order.margin_percentage);
              const isLow = marginClass === 'negative' || marginClass === 'low';
              return (
                <tr key={i} className={`margin-row-${marginClass}`}>
                  <td>
                    <strong>{order.order_number}</strong>
                    {isLow && <span className="low-margin-badge">{t('profitMarginsOverview.lowBadge')}</span>}
                  </td>
                  <td>{order.customer_name}</td>
                  <td title={order.product_names} style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {order.product_names}
                  </td>
                  <td>{formatAppDate(order.order_date)}</td>
                  <td className="text-right">{formatCurrency(order.revenue)}</td>
                  <td className="text-right" style={{ color: 'var(--text-secondary)' }}>{formatCurrency(order.bom_cost)}</td>
                  <td className="text-right" style={{ fontWeight: order.actual_cost > 0 ? '600' : 'normal' }}>{formatCurrency(order.actual_cost)}</td>
                  <td className="text-right waste-cell">{formatCurrency(order.waste_cost)}</td>
                  <td className="text-right">{formatCurrency(order.total_cost)}</td>
                  <td>{renderCostSource(order.cost_source, order.cost_provisional)}</td>
                  <td className="text-right">{formatCurrency(order.margin_amount)}</td>
                  <td className={`text-right ${marginClass}`}>
                    {formatPercentage(order.margin_percentage)}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {t(`costing.orderStatus.${order.status?.toUpperCase()}`, { defaultValue: order.status })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderProductsTable = () => {
    const products = data.products || [];
    if (products.length === 0) {
      return <div className="empty-state">{t('profitMarginsOverview.noProducts')}</div>;
    }
    return (
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t('common:ui.code')}</th>
              <th>{t('common:ui.product')}</th>
              <th className="text-right">{t('costing.columns.revenue')}</th>
              <th className="text-right">{t('costing.columns.cost')}</th>
              <th>{t('costing.columns.costSource')}</th>
              <th className="text-right">{t('costing.columns.waste')}</th>
              <th className="text-right">{t('costing.columns.avgMarginPct')}</th>
              <th className="text-right">{t('costing.columns.orders')}</th>
              <th className="text-right">{t('costing.columns.unitsSold')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((prod, i) => {
              const marginClass = getMarginClass(prod.avg_margin_percentage);
              return (
                <tr key={i} className={`margin-row-${marginClass}`}>
                  <td><strong>{prod.product_code}</strong></td>
                  <td>{prod.product_name}</td>
                  <td className="text-right">{formatCurrency(prod.total_revenue)}</td>
                  <td className="text-right">{formatCurrency(prod.total_cost)}</td>
                  <td>{renderCostSource(prod.cost_source, prod.cost_provisional)}</td>
                  <td className="text-right waste-cell">{formatCurrency(prod.total_waste)}</td>
                  <td className={`text-right ${marginClass}`}>
                    {formatPercentage(prod.avg_margin_percentage)}
                  </td>
                  <td className="text-right">{prod.order_count}</td>
                  <td className="text-right">{formatNumber(prod.units_sold)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCustomersTable = () => {
    const customers = data.customers || [];
    if (customers.length === 0) {
      return <div className="empty-state">{t('profitMarginsOverview.noCustomers')}</div>;
    }
    return (
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>{t('common:ui.customer')}</th>
              <th className="text-right">{t('costing.columns.revenue')}</th>
              <th className="text-right">{t('costing.columns.cost')}</th>
              <th>{t('costing.columns.costSource')}</th>
              <th className="text-right">{t('costing.columns.waste')}</th>
              <th className="text-right">{t('costing.columns.avgMarginPct')}</th>
              <th className="text-right">{t('costing.columns.orders')}</th>
              <th>{t('costing.columns.bestMarginProduct')}</th>
              <th>{t('costing.columns.worstMarginProduct')}</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((cust, i) => {
              const marginClass = getMarginClass(cust.avg_margin_percentage);
              return (
                <tr key={i} className={`margin-row-${marginClass}`}>
                  <td><strong>{cust.customer_name}</strong></td>
                  <td className="text-right">{formatCurrency(cust.total_revenue)}</td>
                  <td className="text-right">{formatCurrency(cust.total_cost)}</td>
                  <td>{renderCostSource(cust.cost_source, cust.cost_provisional)}</td>
                  <td className="text-right waste-cell">{formatCurrency(cust.total_waste)}</td>
                  <td className={`text-right ${marginClass}`}>
                    {formatPercentage(cust.avg_margin_percentage)}
                  </td>
                  <td className="text-right">{cust.order_count}</td>
                  <td>{cust.best_margin_product || '-'}</td>
                  <td>{cust.worst_margin_product || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTable = () => {
    switch (activeView) {
      case 'by-order': return renderOrdersTable();
      case 'by-product': return renderProductsTable();
      case 'by-customer': return renderCustomersTable();
      default: return null;
    }
  };

  return (
    <div className="sales-costing-overview">
      {/* Period Selector */}
      <div className="period-selector">
        <div className="period-toggle">
          <button
            className={`toggle-btn ${!useCustomDates ? 'active' : ''}`}
            onClick={() => setUseCustomDates(false)}
          >
            {t('costing.period.preset')}
          </button>
          <button
            className={`toggle-btn ${useCustomDates ? 'active' : ''}`}
            onClick={() => setUseCustomDates(true)}
          >
            {t('costing.period.custom')}
          </button>
        </div>

        {!useCustomDates ? (
          <>
            <label>{t('costing.period.label')}:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={refreshing}
            >
              <option value="today">{t('costing.period.today')}</option>
              <option value="week">{t('costing.period.week')}</option>
              <option value="month">{t('costing.period.month')}</option>
              <option value="quarter">{t('costing.period.quarter')}</option>
              <option value="year">{t('costing.period.year')}</option>
              <option value="last_month">{t('costing.period.lastMonth')}</option>
              <option value="all">{t('costing.period.all')}</option>
            </select>
          </>
        ) : (
          <>
            <label>{t('costing.period.from')}:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={refreshing}
              className="date-input"
            />
            <label>{t('costing.period.to')}:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={refreshing}
              className="date-input"
            />
          </>
        )}

        <button
          onClick={() => fetchData()}
          disabled={refreshing || (useCustomDates && (!startDate || !endDate))}
          className="btn-refresh"
        >
          {refreshing ? t('costing.refreshing') : t('common:ui.refresh')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card revenue">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <span className="card-label">{t('costing.summary.totalRevenue')}</span>
            <span className="card-value">{formatCurrency(summary.total_revenue)}</span>
            <span className="card-sub">{t('costing.summary.ordersCount', { count: summary.order_count })}</span>
          </div>
        </div>
        <div className="summary-card cost">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <span className="card-label">{t('costing.summary.totalCost')}</span>
            <span className="card-value">{formatCurrency(summary.total_cost)}</span>
            <span className="card-sub cost-breakdown-sub">
              {t('profitMarginsOverview.materialWasteBreakdown', { material: formatCurrency(summary.bom_cost || 0), waste: formatCurrency(summary.waste_cost || 0) })}
            </span>
            {parseFloat(summary.estimated_cost) > 0 && (
              <span className="card-sub cost-breakdown-sub">
                {t('profitMarginsOverview.estimatedPart', { amount: formatCurrency(summary.estimated_cost) })}
              </span>
            )}
          </div>
        </div>
        <div className="summary-card margin">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <span className="card-label">{t('profitMarginsOverview.overallMargin')}</span>
            <span className="card-value">{formatCurrency(summary.gross_margin)}</span>
            <span className={`card-sub margin-pct ${getMarginClass(summary.average_margin_percentage)}`}>
              {t('costing.summary.average', { value: formatPercentage(summary.average_margin_percentage) })}
            </span>
          </div>
        </div>
        <div className="summary-card alerts">
          <div className="card-icon">🔍</div>
          <div className="card-content">
            <span className="card-label">{t('profitMarginsOverview.analyzed')}</span>
            <span className="card-value">{summary.analyzed_count ?? summary.order_count}</span>
            <span className="card-sub">
              {activeView === 'by-order' ? t('profitMarginsOverview.unitOrders') : activeView === 'by-product' ? t('profitMarginsOverview.unitProducts') : t('profitMarginsOverview.unitCustomers')}
            </span>
          </div>
        </div>
      </div>

      {/* Sub-tab Navigation */}
      <div className="orders-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>{t('profitMarginsOverview.title')}</h3>
          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
            {SUB_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleViewChange(tab.id)}
                style={{
                  padding: '0.375rem 0.75rem',
                  border: `1px solid ${activeView === tab.id ? 'var(--primary-color)' : 'var(--border-color)'}`,
                  borderRadius: '4px',
                  background: activeView === tab.id ? 'var(--primary-color)' : 'var(--bg-tertiary)',
                  color: activeView === tab.id ? 'white' : 'var(--text-secondary)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
        </div>
        {renderTable()}
      </div>
    </div>
  );
});

export default ProfitMarginsOverview;
